import React, { memo, useRef, useEffect, useMemo, Suspense, useState, useCallback } from 'react';
import { CircularProgress, Typography, Stack, Popover, IconButton } from '@mui/material';
import clsx from 'clsx';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { LoadingButton } from '@mui/lab';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { ChatMarkdown, TextLoader } from '../../common';
import './SchedulerChatUI.scss';
import { SchedulerChatMessage, useAIScheduler, useAISchedulerChat } from '../../../hooks';
import SchedulerHeroSelection from './SchedulerHeroSelection';
import SchedulerWorkHoursDisplay from './SchedulerWorkHoursDisplay';
import SchedulerStatusMessages from './SchedulerStatusMessages';
import { ConversationQuestionCard } from '../../AiAssistantChat/ConversationQuestionCard';
import { AssistantChatSuggestedBlocks } from '../../AiAssistantChat/AssistantChatSuggestedBlocks';
import { sortSuggestedBlocksByDate } from '../../AiAssistantChat/AssistantChatSuggestedBlocks/helpers';
import {
  ChatSuggestedEvent,
  isRunningAISchedulerAtom,
  settingsSidebarStateAtom,
  FirestoreRoutineActivity,
  aiSchedulerStatusTextAtom,
  useAssistantChat,
  useLanguageContext,
} from '../../../data-access';
import { AssistantChatHeader } from '../../AiAssistantChat/AssistantChatHeader';
import { ChatInput } from '../ChatInput';
import { SessionResponse } from '../../../data-access';
import {
  SchedulerTaskPref,
  SchedulerRoutineChatPref,
  TasksPrefItemState,
  RoutinePrefItemState,
  WorkHourPrefItemState,
  AISchedulerFeedbackDialog,
} from '../../Scheduler';
import { CalendarEvent } from '@demind-inc/core';
import { useFeatureFlag } from '../../../utils';
import { useNavigate } from 'react-router-dom';
import { Path } from '../../../routing';
import { ArrowBack, FeedbackOutlined } from '@mui/icons-material';
import { SchedulerScope } from '../../../hooks/schedulerChat/types';
import { PlanResultSource } from '../../../helpers/scheduler.helpers';
import WeekDatePickerModal from './WeekDatePickerModal';
import { DateCalendar } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { getDayjsLocaleForLanguage } from '../../../i18n/dayjs';

const LIFESTACK_DESCRIPTION_FOOTER = '[Created from Lifestack (https://lifestack.ai)].';

const sanitizeExplanation = (text: unknown) => {
  if (typeof text !== 'string') return '';
  return text.replaceAll(LIFESTACK_DESCRIPTION_FOOTER, '').trim();
};

const EMPTY_PLAN_RESULT_SOURCES: Record<string, PlanResultSource> = {};

const mapScheduledEventsToSuggestedBlocks = (
  events: CalendarEvent[],
  planResultSourceById: Record<string, PlanResultSource> = EMPTY_PLAN_RESULT_SOURCES
): ChatSuggestedEvent[] =>
  sortSuggestedBlocksByDate(
    events.map((event) => {
      const eventId = event.autoScheduleFrom?.id || event.eventId;
      const mappedSource = eventId ? planResultSourceById[eventId] : undefined;
      const originKind: PlanResultSource | undefined =
        mappedSource ??
        (event.taskFrom || event.autoScheduleFrom?.from === 'todo'
          ? 'todo'
          : event.routineFrom || event.autoScheduleFrom?.from === 'routine'
            ? 'routine'
            : undefined);
      return {
        eventId,
        name: event.summary || '',
        startTime: event.start?.date,
        endTime: event.end?.date,
        date: event.start?.date ? dayjs(event.start.date).format('YYYY-MM-DD') : undefined,
        description: sanitizeExplanation((event as { description?: unknown }).description),
        autoScheduleFrom: event.autoScheduleFrom,
        originKind,
        action: 'setEvent' as const,
      };
    })
  );

const NOOP_PROCESSED_IDS_CHANGE = () => undefined;

interface SchedulerChatUIProps {
  messages: SchedulerChatMessage[];
  onRunScheduler: (userMessage?: string) => Promise<string>;
  onAdjustScheduler: (
    message: string,
    chatSessionId?: string,
    onMessage?: (delta: string) => void,
    onQuestion?: (question: any) => void,
    hiddenContext?: Record<string, unknown>,
    onWholeMessage?: (message: string, textId: number) => void,
    onSuggestedBlocks?: (blocks: ChatSuggestedEvent[]) => void
  ) => Promise<SessionResponse | undefined>;
  onCustomRuleInput?: (text: string) => void;
  isInitializing?: boolean;
  canSchedule?: boolean;
  currentMessageIndex?: number;
  totalMessages?: number;
  isTyping?: boolean;
  showRefresh?: boolean;
  className?: string;
  onClearPress?: () => void;
  containerHeight?: number;
  selectedDate: Dayjs;
  onSelectedDateChange: (date: Dayjs) => void;
  rangeEndDate?: Dayjs;
  onRangeDateChange?: (startDate: Dayjs, endDate: Dayjs) => void;
  tasks: TasksPrefItemState;
  routines: RoutinePrefItemState;
  targetDate: string;
  hasScheduledActions?: boolean;
  onTaskSelectionChange: (taskId: string, selected: boolean) => void;
  onTaskChange: (taskId: string, pref: any) => void;
  onRoutineSelectionChange: (routineId: string, selected: boolean) => void;
  onRoutineChange: (routineId: string, pref: FirestoreRoutineActivity) => void;
  onRoutineAdd: (pref: FirestoreRoutineActivity) => void;
  onRoutineDelete: (routineId: string) => void;
  tasksLoading?: boolean;
  routineLoading?: boolean;
  isV5Flow?: boolean;
  workHours?: WorkHourPrefItemState;
  onWorkHourChange?: (pref: WorkHourPrefItemState) => void;
  workHoursLoading?: boolean;
  existingRoutineEvents?: CalendarEvent[];
  scheduledRoutineActivityIds?: Set<string>;
  scheduledEvents?: CalendarEvent[];
  /** Task/routine origin keyed by autoScheduleFrom.id, for the result-card icon. */
  planResultSourceById?: Record<string, PlanResultSource>;
  onDiscardScheduledEvent?: (actionId: string) => void;
  onDiscardAllScheduledEvents?: (actionIds: string[]) => void;
  onAcceptScheduledEvent?: (event: CalendarEvent) => Promise<void> | void;
  onSaveScheduledEvents?: () => Promise<void> | void;
  schedulerScope?: SchedulerScope;
  defaultRunMessage?: string;
  defaultRunDisplayMessage?: string;
}

const SchedulerChatUI: React.FC<SchedulerChatUIProps> = memo(
  ({
    messages,
    onRunScheduler,
    onAdjustScheduler,
    onCustomRuleInput = () => void 0,
    isInitializing = false,
    canSchedule = true,
    currentMessageIndex = 0,
    totalMessages = 0,
    showRefresh = false,
    hasScheduledActions = false,
    className,
    onClearPress = () => void 0,
    containerHeight,
    selectedDate,
    onSelectedDateChange,
    rangeEndDate,
    onRangeDateChange,
    tasks,
    routines,
    targetDate,
    onTaskSelectionChange,
    onTaskChange,
    onRoutineSelectionChange,
    onRoutineChange,
    onRoutineAdd,
    onRoutineDelete,
    tasksLoading = false,
    routineLoading = false,
    isV5Flow = false,
    workHours,
    onWorkHourChange,
    workHoursLoading = false,
    existingRoutineEvents = [],
    scheduledRoutineActivityIds = new Set(),
    scheduledEvents = [],
    planResultSourceById = EMPTY_PLAN_RESULT_SOURCES,
    onDiscardScheduledEvent,
    onDiscardAllScheduledEvents,
    onAcceptScheduledEvent,
    schedulerScope = 'day',
    defaultRunMessage,
    defaultRunDisplayMessage,
  }) => {
    const { t } = useTranslation();
    const { language } = useLanguageContext();
    const dateLocale = getDayjsLocaleForLanguage(language);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const isRunningAIScheduler = useRecoilValue(isRunningAISchedulerAtom);
    const aiSchedulerStatusText = useRecoilValue(aiSchedulerStatusTextAtom);
    const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
    const navigate = useNavigate();
    const { setOpenAssistantChat } = useAssistantChat();
    const {
      featureFlags: { FF_ai_scheduler_adjust_v2 },
    } = useFeatureFlag();
    // State to track if scheduler has completed and we should show chat interface
    const [schedulerCompleted, setSchedulerCompleted] = useState(hasScheduledActions);
    const [processedSuggestedIds, setProcessedSuggestedIds] = useState<string[]>([]);
    const [capturedSuggestedBlocks, setCapturedSuggestedBlocks] = useState<ChatSuggestedEvent[]>([]);
    const {
      aiFeedbackDialogVisible,
      handleFeedbackDialogClose,
      handleDiscard,
      handleConfirmDiscard,
      stopAiScheduler,
    } = useAIScheduler();
    const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false);
    const [taskModalAnchor, setTaskModalAnchor] = useState<HTMLElement | null>(null);
    const [routineModalAnchor, setRoutineModalAnchor] = useState<HTMLElement | null>(null);
    const [heroLocked, setHeroLocked] = useState(false);
    const [customRuleInput, setCustomRuleInput] = useState('');
    const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);

    // Use the AI scheduler chat hook for chat functionality
    const {
      messages: aiSchedulerMessages,
      sendMessage,
      sendQuestionAnswer,
      isReplying,
      isQuestionPending,
      startChat,
      closeChat,
    } = useAISchedulerChat({
      onReschedule: onAdjustScheduler,
      enabled: true,
    });

    useEffect(() => {
      if (hasScheduledActions) {
        setSchedulerCompleted(true);
      }
    }, [hasScheduledActions]);

    useEffect(() => {
      if (!isV5Flow || currentMessageIndex <= 1 || schedulerCompleted) {
        setHeroLocked(false);
      }
    }, [isV5Flow, currentMessageIndex, schedulerCompleted]);

    // Auto-scroll to end when messages change
    useEffect(() => {
      if (chatContainerRef.current) {
        setTimeout(() => {
          chatContainerRef.current?.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }, 100);
      }
    }, [
      messages.length,
      aiSchedulerMessages.length,
      aiSchedulerStatusText.length,
    ]);

    const taskEntries = useMemo(() => Object.values(tasks ?? {}), [tasks]);
    const routineEntries = useMemo(() => Object.values(routines ?? {}), [routines]);
    const selectedTaskNames = useMemo(
      () =>
        taskEntries
          .filter((task) => task.selected)
          .map((task) => task.name)
          .filter((name): name is string => !!name?.trim()),
      [taskEntries]
    );
    const selectedRoutineNames = useMemo(
      () =>
        routineEntries
          .filter((routine) => routine.selected)
          .map((routine) => routine.name)
          .filter((name): name is string => !!name?.trim()),
      [routineEntries]
    );

    const selectionRequired = taskEntries.length > 0 || routineEntries.length > 0;
    const hasSelections =
      !selectionRequired || selectedTaskNames.length > 0 || selectedRoutineNames.length > 0;
    const disableSelectionControls =
      isInitializing ||
      tasksLoading ||
      routineLoading ||
      isRunningAIScheduler ||
      heroLocked ||
      aiSchedulerStatusText.length > 0;
    const effectiveRangeEndDate = useMemo(() => {
      if (!rangeEndDate || rangeEndDate.isBefore(selectedDate, 'day')) {
        return selectedDate;
      }
      return rangeEndDate;
    }, [rangeEndDate, selectedDate]);

    const scheduleLabel = useMemo(() => {
      if (schedulerScope === 'week') {
        const formatWeekLabel = (date: Dayjs, allowToday = false) => {
          if (allowToday && date.isSame(dayjs(), 'day')) {
            return t('calendar.common.today');
          }
          const isInCurrentWeek = date.isSame(dayjs(), 'week');

          return isInCurrentWeek
            ? date.locale(dateLocale).format('ddd')
            : date.locale(dateLocale).format('MMM DD');
        };

        const startLabel = formatWeekLabel(selectedDate, true);
        const endLabel = formatWeekLabel(effectiveRangeEndDate);
        return `${startLabel} - ${endLabel}`;
      }
      const today = dayjs().startOf('day');
      const tomorrow = today.add(1, 'day');
      const dateToCheck = selectedDate.startOf('day');

      if (dateToCheck.isSame(today)) {
        return t('calendar.common.today');
      }
      if (dateToCheck.isSame(tomorrow)) {
        return t('calendar.common.tomorrow');
      }
      return selectedDate.locale(dateLocale).format('MMM DD');
    }, [dateLocale, effectiveRangeEndDate, schedulerScope, selectedDate, t]);

    useEffect(() => {
      if (scheduledEvents.length > 0) {
        setCapturedSuggestedBlocks((prev) => {
          const newBlocks = mapScheduledEventsToSuggestedBlocks(scheduledEvents, planResultSourceById);
          if (prev.length === 0) {
            return newBlocks;
          }
          const existingIds = new Set(prev.map((b) => b.eventId));
          const additions = newBlocks.filter((b) => !existingIds.has(b.eventId));
          return additions.length > 0 ? sortSuggestedBlocksByDate([...prev, ...additions]) : prev;
        });
      }
    }, [scheduledEvents, planResultSourceById]);

    const suggestedBlocks = useMemo(() => {
      if (capturedSuggestedBlocks.length > 0) {
        return sortSuggestedBlocksByDate(capturedSuggestedBlocks);
      }
      return mapScheduledEventsToSuggestedBlocks(scheduledEvents, planResultSourceById);
    }, [capturedSuggestedBlocks, planResultSourceById, scheduledEvents]);

    const shouldShowScheduledEvents =
      schedulerCompleted && (suggestedBlocks.length > 0 || scheduledEvents.length > 0);

    const handleAcceptSuggestedBlock = useCallback(
      async (block: ChatSuggestedEvent): Promise<boolean> => {
        const event = scheduledEvents.find(
          (item) => item.autoScheduleFrom?.id === block.eventId || item.eventId === block.eventId
        );
        if (!event || !onAcceptScheduledEvent) {
          return false;
        }
        await onAcceptScheduledEvent(event);
        setProcessedSuggestedIds((prev) =>
          prev.includes(block.eventId) ? prev : [...prev, block.eventId]
        );
        return true;
      },
      [onAcceptScheduledEvent, scheduledEvents]
    );

    const handleDiscardSuggestedBlock = useCallback(
      (eventId: string) => {
        onDiscardScheduledEvent?.(eventId);
        setProcessedSuggestedIds((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
      },
      [onDiscardScheduledEvent]
    );

    const handleDiscardAllSuggestedBlocks = useCallback(
      (eventIds: string[]) => {
        if (onDiscardAllScheduledEvents) {
          onDiscardAllScheduledEvents(eventIds);
        } else {
          eventIds.forEach((id) => onDiscardScheduledEvent?.(id));
        }
        setProcessedSuggestedIds((prev) => Array.from(new Set([...prev, ...eventIds])));
      },
      [onDiscardAllScheduledEvents, onDiscardScheduledEvent]
    );

    const handleOpenDatePicker = (event: React.MouseEvent<HTMLElement>) => {
      setIsDatePickerModalOpen(true);
      setDatePickerAnchor(event.currentTarget);
    };

    const handleCloseDatePicker = () => {
      setIsDatePickerModalOpen(false);
      setDatePickerAnchor(null);
    };

    const handleOpenTaskModal = (event: React.MouseEvent<HTMLElement>) => {
      setTaskModalAnchor(event.currentTarget);
    };

    const handleCloseTaskModal = () => {
      setTaskModalAnchor(null);
    };

    const handleOpenRoutineModal = (event: React.MouseEvent<HTMLElement>) => {
      setRoutineModalAnchor(event.currentTarget);
    };

    const handleCloseRoutineModal = () => {
      setRoutineModalAnchor(null);
    };

    // Handle scheduler completion
    // Set when the user stops a run, so the thread says so instead of
    // silently falling back to the pre-run controls.
    const [wasCancelled, setWasCancelled] = useState(false);
    const wasCancelledRef = useRef(false);
    const handleStopScheduler = () => {
      wasCancelledRef.current = true;
      setWasCancelled(true);
      stopAiScheduler();
    };

    const handleSchedulerRun = async () => {
      wasCancelledRef.current = false;
      setWasCancelled(false);
      const userMessage = customRuleInput.trim() || undefined;
      const effectiveMessage = userMessage || defaultRunMessage;
      setCapturedSuggestedBlocks([]);
      setProcessedSuggestedIds([]);
      const summary = await onRunScheduler(effectiveMessage);
      if (wasCancelledRef.current) {
        return;
      }
      setCustomRuleInput('');
      startChat(summary, userMessage ? undefined : defaultRunDisplayMessage);
    };

    // Handle sending messages in the chat interface
    const handleSendMessage = async (message: string) => {
      try {
        await sendMessage(message);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    };

    const handleChatClose = () => {
      setSchedulerCompleted(false);
      setCapturedSuggestedBlocks([]);
      setProcessedSuggestedIds([]);
      // Abort/reset first, then clear the chat thread — reversing this lets
      // late stream callbacks repopulate messages after New Chat.
      onClearPress();
      closeChat();
    };

    function handleOpenSettings() {
      setSettingsSidebarState({
        selectedParentKey: 'aiAssistant',
        expandedParentKeys: [],
      });
      navigate(`${Path.HOME}/settings`);
      setOpenAssistantChat(false);
    }

    const renderSchedulerMessage = (item: SchedulerChatMessage) => {
      const isUser = item.type === 'user';
      const isWelcomeMessage = item.id === 'welcome';

      if (isUser) {
        return (
          <div key={item.id} className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--user">
            <div className="scheduler-chat-ui__bubble_content">
              <Typography
                variant="body1"
                className="scheduler-chat-ui__bubble_text scheduler-chat-ui__bubble_text--user"
              >
                {item.content}
              </Typography>
            </div>
          </div>
        );
      }

      // Week welcome: "Let's schedule…" in its own AI bubble, then the Week Plan
      // hero card in a second bubble (no work-hours row). Day welcome stays
      // as a single combined bubble for backward compatibility.
      if (isWelcomeMessage && schedulerScope === 'week') {
        return (
          <React.Fragment key={item.id}>
            <div className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai">
              <div className="scheduler-chat-ui__bubble_content">
                <Suspense fallback={<CircularProgress size={22} />}>
                  <ChatMarkdown content={item.content} />
                </Suspense>
                {!canSchedule && (
                  <Typography variant="caption" className="scheduler-chat-ui__no_schedule_text">
                    {t('task.scheduler.noPlansWorkHoursEnded')}
                  </Typography>
                )}
              </div>
            </div>
            <div className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai-fill">
              <div className="scheduler-chat-ui__bubble_content">
                <SchedulerHeroSelection
                  heroLocked={heroLocked}
                  scheduleLabel={scheduleLabel}
                  disableSelectionControls={disableSelectionControls}
                  taskEntriesLength={taskEntries.length}
                  routineEntriesLength={routineEntries.length}
                  selectedTaskCount={selectedTaskNames.length}
                  selectedRoutineCount={selectedRoutineNames.length}
                  onOpenDatePicker={handleOpenDatePicker}
                  onOpenTaskModal={handleOpenTaskModal}
                  onOpenRoutineModal={handleOpenRoutineModal}
                  showDatePickerControl
                  customRuleInput={customRuleInput}
                  onCustomRuleInputChange={setCustomRuleInput}
                  isDatePickerOpen={isDatePickerModalOpen}
                  isTaskModalOpen={Boolean(taskModalAnchor)}
                  isRoutineModalOpen={Boolean(routineModalAnchor)}
                  schedulerScope={schedulerScope}
                />
              </div>
            </div>
          </React.Fragment>
        );
      }

      return (
        <div key={item.id} className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai">
          <div className="scheduler-chat-ui__bubble_content">
            <Suspense fallback={<CircularProgress size={22} />}>
              <ChatMarkdown content={item.content} />
            </Suspense>
            {item.component && <div>{item.component}</div>}
            {isWelcomeMessage && workHours && onWorkHourChange && (
              <div className="scheduler-chat-ui__workhours_selector">
                <SchedulerWorkHoursDisplay
                  workHours={workHours}
                  onWorkHourChange={onWorkHourChange}
                  isLoading={workHoursLoading}
                  disabled={disableSelectionControls}
                  onOpenSettings={handleOpenSettings}
                />
              </div>
            )}
            {isWelcomeMessage && !canSchedule && (
              <Typography variant="caption" className="scheduler-chat-ui__no_schedule_text">
                {t('task.scheduler.noPlansWorkHoursEnded')}
              </Typography>
            )}
            {isWelcomeMessage && (
              <SchedulerHeroSelection
                heroLocked={heroLocked}
                scheduleLabel={scheduleLabel}
                disableSelectionControls={disableSelectionControls}
                taskEntriesLength={taskEntries.length}
                routineEntriesLength={routineEntries.length}
                selectedTaskCount={selectedTaskNames.length}
                selectedRoutineCount={selectedRoutineNames.length}
                onOpenDatePicker={handleOpenDatePicker}
                onOpenTaskModal={handleOpenTaskModal}
                onOpenRoutineModal={handleOpenRoutineModal}
                showDatePickerControl
                customRuleInput={customRuleInput}
                onCustomRuleInputChange={setCustomRuleInput}
                isDatePickerOpen={isDatePickerModalOpen}
                isTaskModalOpen={Boolean(taskModalAnchor)}
                isRoutineModalOpen={Boolean(routineModalAnchor)}
                schedulerScope={schedulerScope}
              />
            )}
          </div>
        </div>
      );
    };

    const renderAssistantMessage = (message: any) => {
      const isUser = message.role === 'user';

      if (isUser) {
        return (
          <div
            key={message.messageId}
            className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--user"
          >
            <div className="scheduler-chat-ui__bubble_content">
              <Typography
                variant="body1"
                className="scheduler-chat-ui__bubble_text scheduler-chat-ui__bubble_text--user"
              >
                {message.content}
              </Typography>
            </div>
          </div>
        );
      }

      // Question card (conflict resolution)
      if (message.question) {
        return (
          <div
            key={message.messageId}
            className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai"
          >
            <div className="scheduler-chat-ui__bubble_content">
              <ConversationQuestionCard
                question={message.question}
                onSelect={(option) => sendQuestionAnswer(message.messageId, option)}
                disabled={Boolean(message.isAnswered)}
              />
            </div>
          </div>
        );
      }

      if (message.suggestedBlocks?.length) {
        return (
          <div key={message.messageId} className="scheduler-chat-ui__suggested_blocks">
            <div className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai scheduler-chat-ui__bubble--ai-fill scheduler-chat-ui__bubble--suggested-blocks">
              <div className="scheduler-chat-ui__bubble_content">
                <AssistantChatSuggestedBlocks
                  blocks={message.suggestedBlocks}
                  processedIds={processedSuggestedIds}
                  onProcessedIdsChange={setProcessedSuggestedIds}
                  onManageCalendar={handleAcceptSuggestedBlock}
                  variant="screen"
                  onDiscardBlock={handleDiscardSuggestedBlock}
                  onDiscardAllBlocks={handleDiscardAllSuggestedBlocks}
                />
              </div>
            </div>
          </div>
        );
      }

      // AI bubble
      return (
        <div
          key={message.messageId}
          className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai"
        >
          <div className="scheduler-chat-ui__bubble_content">
            <Suspense fallback={<CircularProgress size={22} />}>
              <ChatMarkdown content={message.content} />
            </Suspense>
          </div>
        </div>
      );
    };

    const schedulerMessages = messages.map(renderSchedulerMessage);
    const initialSchedulerChatMessageCount =
      schedulerCompleted && aiSchedulerMessages[0]?.role === 'user' ? 2 : 1;
    const initialSchedulerChatMessages = schedulerCompleted
      ? aiSchedulerMessages.slice(0, initialSchedulerChatMessageCount)
      : [];
    const followUpSchedulerChatMessages = schedulerCompleted
      ? aiSchedulerMessages.slice(initialSchedulerChatMessageCount)
      : [];
    // aiSchedulerMessages is only seeded once a run finishes via this
    // component's own handleSchedulerRun (startChat). A session resumed
    // straight into the completed state (job-recovery restoring a plan that
    // already finished) never calls that, so it has none — fall back to the
    // visibleMessages-driven intro turn (schedulerMessages) so the original
    // "Plan my week" prompt still shows instead of nothing.
    const hasOwnCompletionThread = aiSchedulerMessages.length > 0;

    return (
      <div className={clsx('scheduler-chat-ui', className)}>
        {/* Header */}
        <AssistantChatHeader
          className="scheduler-chat-ui__header"
          showBackButton={false}
          showMenu={false}
          onOpenSettings={handleOpenSettings}
          onRefresh={handleChatClose}
          onBackPress={() => {
            setSchedulerCompleted(false);
            setCapturedSuggestedBlocks([]);
            setProcessedSuggestedIds([]);
            onClearPress();
          }}
        />

        {/* Chat Container — explanation bubble first, then plan-my-day-style
            suggested event blocks + Approve/Dismiss CTAs. */}
        <div
          ref={chatContainerRef}
          className={clsx('scheduler-chat-ui__chat_container', {
            'scheduler-chat-ui__chat_container--with-suggestions': shouldShowScheduledEvents,
          })}
        >
          <div
            className="scheduler-chat-ui__messages"
            style={{
              maxHeight: containerHeight ? `${containerHeight - 120}px` : undefined,
            }}
          >
            {!schedulerCompleted || !hasOwnCompletionThread
              ? schedulerMessages
              : initialSchedulerChatMessages.map(renderAssistantMessage)}
            {shouldShowScheduledEvents && (
              <div className="scheduler-chat-ui__suggested_blocks">
                {/* Lavender AiChatBubble chrome — same as PlanPreviewBubble /
                    suggested-blocks sections so week results sit inset like
                    plan-my-day changes. */}
                <div className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai scheduler-chat-ui__bubble--ai-fill scheduler-chat-ui__bubble--suggested-blocks">
                  <div className="scheduler-chat-ui__bubble_content">
                    <AssistantChatSuggestedBlocks
                      blocks={suggestedBlocks}
                      processedIds={processedSuggestedIds}
                      onProcessedIdsChange={setProcessedSuggestedIds}
                      onManageCalendar={handleAcceptSuggestedBlock}
                      variant="screen"
                      onDiscardBlock={handleDiscardSuggestedBlock}
                      onDiscardAllBlocks={handleDiscardAllSuggestedBlocks}
                    />
                  </div>
                </div>
              </div>
            )}
            {schedulerCompleted && followUpSchedulerChatMessages.map(renderAssistantMessage)}
            {isReplying && !FF_ai_scheduler_adjust_v2 && (
              <div
                className="scheduler-chat-ui__bubble scheduler-chat-ui__bubble--ai"
                style={{ width: 'fit-content' }}
              >
                <div className="scheduler-chat-ui__bubble_content" style={{ alignItems: 'center' }}>
                  <TextLoader />
                </div>
              </div>
            )}
            {/* Adjustment follow-up status uses the same stacked stream UI as
                initial plan-my-day / plan-my-week generation. */}
            {schedulerCompleted && aiSchedulerStatusText.length > 0 && (
              <SchedulerStatusMessages statusLines={aiSchedulerStatusText} />
            )}

            {/* Plan-my-day / plan-my-week streaming status — up to 3 lines,
                newest enters from bottom, oldest fades out upward. */}
            {!schedulerCompleted && aiSchedulerStatusText.length > 0 && (
              <SchedulerStatusMessages statusLines={aiSchedulerStatusText} />
            )}
            {wasCancelled && !isRunningAIScheduler && (
              <div className="scheduler-chat-ui__cancelled">{t('task.ai.input.cancelled')}</div>
            )}
          </div>
        </div>

        {/* Loading overlay for initialization */}
        {isInitializing && (
          <div className="scheduler-chat-ui__loading_overlay">
            <CircularProgress size={32} />
          </div>
        )}

        {/* Action buttons or Chat Input */}
        {!schedulerCompleted ? (
          // Run + back before planning; while status is streaming keep the
          // field editable so the user can draft, but block send mid-plan.
          aiSchedulerStatusText.length > 0 || isRunningAIScheduler ? (
            <div className="scheduler-chat-ui__chat_input_container">
              <ChatInput
                onSendMessage={() => undefined}
                disabled
                onStop={handleStopScheduler}
                placeholder={t('task.scheduler.askToAdjustSchedule')}
              />
            </div>
          ) : (
            <div className="scheduler-chat-ui__action_container">
              <div className="scheduler-chat-ui__button_group">
                <IconButton
                  className="scheduler-chat-ui__back-icon-button"
                  onClick={handleChatClose}
                >
                  <ArrowBack className="scheduler-chat-ui__back-icon-button__icon" />
                </IconButton>
                <LoadingButton
                  variant="contained"
                  onClick={handleSchedulerRun}
                  disabled={
                    !hasSelections ||
                    isInitializing ||
                    isRunningAIScheduler ||
                    tasksLoading ||
                    routineLoading ||
                    !canSchedule
                  }
                  className="scheduler-chat-ui__run_button"
                  loading={isRunningAIScheduler}
                >
                  <Typography variant="body1" className="scheduler-chat-ui__run_button_text">
                    {t('task.scheduler.run')}
                  </Typography>
                </LoadingButton>
              </div>
            </div>
          )
        ) : (
          // Show chat input after scheduler completes
          <div className="scheduler-chat-ui__chat_input_container">
            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={
                isReplying ||
                isRunningAIScheduler ||
                isQuestionPending ||
                aiSchedulerStatusText.length > 0
              }
              placeholder={t('task.scheduler.askToAdjustSchedule')}
            />
          </div>
        )}
        {schedulerScope === 'week' ? (
          <WeekDatePickerModal
            isOpen={isDatePickerModalOpen}
            onClose={handleCloseDatePicker}
            selectedDate={selectedDate}
            endDate={effectiveRangeEndDate}
            onSelectWeek={(startDate, endDate) => {
              if (onRangeDateChange) {
                onRangeDateChange(startDate, endDate);
                return;
              }
            }}
          />
        ) : (
          <Popover
            open={Boolean(datePickerAnchor)}
            anchorEl={datePickerAnchor}
            onClose={handleCloseDatePicker}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            className="scheduler-chat-ui__popover"
          >
            <DateCalendar
              value={selectedDate}
              onChange={(val) => {
                onSelectedDateChange(val);
                handleCloseDatePicker();
              }}
              shouldDisableDate={(date) => date.isBefore(dayjs().startOf('day'))}
            />
          </Popover>
        )}

        {/* Task Popover */}
        <Popover
          open={Boolean(taskModalAnchor)}
          anchorEl={taskModalAnchor}
          onClose={handleCloseTaskModal}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          className="scheduler-chat-ui__popover"
        >
          <SchedulerTaskPref
            tasks={tasks}
            targetDate={targetDate}
            onTaskChange={onTaskChange}
            isLoading={tasksLoading}
            onCheckTask={onTaskSelectionChange}
            className="scheduler-chat-ui__popover__content"
          />
        </Popover>

        {/* Routine Popover */}
        <Popover
          open={Boolean(routineModalAnchor)}
          anchorEl={routineModalAnchor}
          onClose={handleCloseRoutineModal}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          className="scheduler-chat-ui__popover"
        >
          <SchedulerRoutineChatPref
            routine={routines}
            isLoading={routineLoading}
            onRoutineCheckChange={onRoutineSelectionChange}
            onRoutineChange={onRoutineChange}
            onRoutineAdd={onRoutineAdd}
            onRoutineDelete={onRoutineDelete}
            hasConfigured={false}
            fontSize="small"
            className="scheduler-chat-ui__popover__content"
            existingRoutineEvents={existingRoutineEvents}
            scheduledRoutineActivityIds={scheduledRoutineActivityIds as Set<string>}
          />
        </Popover>
        <AISchedulerFeedbackDialog
          visible={aiFeedbackDialogVisible}
          onClose={handleFeedbackDialogClose}
          onDiscard={handleConfirmDiscard}
        />
      </div>
    );
  }
);

export default SchedulerChatUI;
